import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../models/base-modal-component';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, HelpIndex, MembershipType, ReviewSystem } from '../../../models/enums';
import { CellTemplateType, GenericColDef, GridContext } from '../../../models/grid-context';
import { CommonService } from '../../../services/common/common.service';
import { RestService } from '../../../services/rest/rest.service';
import { GenericGridComponent } from '../../generic-grid/generic-grid.component';
import { NameDescriptionComponent } from '../../name-description/name-description.component';
import { MembershipModalComponent } from '../membership-modal/membership-modal.component';
import { ProjectViewModel } from '../../../models/project';
import { Permissions, ObjectActionListType, ActionType } from '../../../models/permission';
import { AutoProjectCreation } from '../../../models/preferences';
import { isNullOrUndefined } from '../../../models/utils';
import { DrObject } from '../../../models/dr-object';
import { SelectReviewProjectsModalComponent } from '../select-review-projects-modal/select-review-projects-modal.component';
import { SelectReviewTemplateModalComponent } from '../select-review-template-modal/select-review-template-modal.component';

@Component({
    standalone: false,
    selector: 'res-new-project-modal',
    templateUrl: './new-project-modal.component.html',
    styleUrls: ['./new-project-modal.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class NewProjectModalComponent extends BaseModalComponent implements OnInit {
    @ViewChild(NameDescriptionComponent, { static: true }) nameDescriptionComponent: NameDescriptionComponent;
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    validateNameRequest = {
        endpoint: Endpoint.viewManager_validateName,
        request: (name) => {
            return {
                name: name,
                organizationName: this.commonService.currentUser.customerName,
                objectType: this.nameDescriptionComponent.drObject.objectType
            };
        }
    };

    isBusy = false;
    isBusyMessage: string;
    isSettingsBusy = false;
    isExpressSystem = false;
    reviewSystem: any = ReviewSystem.NONE;
    eDiscoveryTemplateTypes: any;
    reviewTemplateTypes: any;

    roleList: Array<any>;
    allMembers: Array<any>;
    modalClass = 'new-project-modal';
    modalAutomationId = 'new-project';
    modalHelpIndex = HelpIndex.create_case_dlg;
    logoModel = { encodedData: null, fileName: "" };
    reviewSystemEnum = ReviewSystem;
    reefReviewEnabled = false;
    userValid = true;
    autoGenerate = true;
    reefReviewDisplayName = ReviewSystem.REEF_REVIEW.split(/(?=[A-Z])/).join(" ");
    availableReviewProjects = [];
    selectedReviewProjects = [];

    hasSysPermissions = false;
    hasRolePermissions = false;
    standardProjectType = true;
    selectedTemplate;
    availableReviewTemplates = [];
    modalOpened;

    private roleHierarchy = [
        "Organization Administrator",
        "Project Administrator",
        "Project Member"
    ];

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Type',
            field: 'type',
            sortBy: 'type',
            automationId: 'new-project',
            cellTemplateType: CellTemplateType.nameIconLabel,
            width: 180,
            lockVisible: true
        },
        {
            headerName: 'Name',
            field: 'name',
            sortBy: 'name',
            automationId: 'new-project',
            lockVisible: true
        },
        {
            headerName: 'Role',
            automationId: 'new-project',
            cellTemplateType: CellTemplateType.roleDropdown,
            width: 320,
            lockVisible: true,
            cellStyle: () => {
                if (!this.hasRolePermissions) {
                    return { "pointer-events": "none", opacity: "0.4" };
                }
                return null;
            },
        }
    );
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.orgManager_listUsersAndGroups,
        data: { organizationName: this.commonService.currentUser.customerName },
        responseName: 'members'
    });
    gridContext = new GridContext({
        sortBy: 'name',
        sortAscending: true
    });
    responseCallback = (response: any) => {
        if (response) {
            response.members = [];
            this.allMembers = [];
            if (response.groups) {
                response.groups.forEach(entity => {
                    entity.type = entity.importedFromSystemGroup ? 'System Group' : 'Group';
                    entity.iconClass = `fa-users ${entity.importedFromSystemGroup ? ' color-purple-03' : ' color-yellow-07'}`;
                    if (this.roleList) {
                        entity.roleList = this.roleList;
                        entity.initialRole = entity.selectedRole = this.getUserRole(entity.roleHandles);
                    }
                    this.allMembers.push(entity);
                });
            }

            this.assignHighestRoleToCurrentUser(response);

            if (response.users) {
                response.users.forEach(entity => {
                    entity.type = entity.importedFromSystemUser ? 'System User' : 'User';
                    entity.iconClass = `fa-user ${entity.importedFromSystemUser ? ' color-purple-03' : ' color-yellow-07'}`;
                    entity.name = entity.userName;
                    if (this.roleList) {
                        entity.roleList = this.roleList;
                        entity.initialRole = entity.selectedRole = this.getUserRole(entity.roleHandles);
                    }
                    if (entity.roles?.length > 0) {
                        this.allMembers.push(entity);
                    }
                    if (entity.userName === this.commonService.currentUser.userName) {
                        response.members.push(entity);
                    }
                });
            }
        }
    }

    private assignHighestRoleToCurrentUser(response: any): void {
        let selectedRole: any = null;
        let selectedGroup: any = null;

        if (this.roleList){
            response.groups.forEach(group => {
                if (group.users && group.users?.includes(this.commonService.currentUser.userName)) {
                    const role = this.getUserRole(group.roleHandles);
                    if (role) {
                        const currentIndex = this.roleHierarchy.indexOf(selectedRole?.name ?? "");
                        const newIndex = this.roleHierarchy.indexOf(role.name);

                        if (newIndex !== -1) {
                            // role is in hierarchy → compare
                            if (currentIndex === -1 || newIndex < currentIndex) {
                                selectedRole = role;
                                selectedGroup = group;
                            } else if (newIndex === currentIndex) {
                                selectedGroup = group; // same level → keep group
                            }
                        } else if (!selectedRole) {
                            // role is outside hierarchy → fallback
                            selectedRole = role;
                            selectedGroup = group;
                        }
                    }
                }
            });

            // if we found something, add/update user
            if (selectedRole && selectedGroup) {
                const currentUserExists = response.users?.filter((entity: any) => entity.userName === this.commonService.currentUser.userName).pop();

                if (!currentUserExists) {
                    const user = structuredClone(this.commonService.currentUser);
                    user.importedFromSystemUser = true;
                    user.roleHandles = selectedGroup.roleHandles;
                    user.roles = selectedGroup.roles;
                    response.users.push(user);
                } else {
                    // optional: update roleHandles if already exists
                    currentUserExists.roleHandles = selectedGroup.roleHandles;
                    currentUserExists.roles = selectedGroup.roles;
                }
            }
        }
    }

    constructor(
        private modalService: BsModalService,
        private modalRef: BsModalRef,
        private restService: RestService,
        private commonService: CommonService,
        private router: Router) {
        super(modalService, modalRef);
    }

    ngOnInit(): void {
        this.nameDescriptionComponent.drObject = new ProjectViewModel();
        this.modalOpened = true;
        this.loadTemplates();
        this.loadRoles();
        this.isExpressSystem = this.modalData.isExpressSystem || false;
        this.reefReviewEnabled = this.commonService.reefReviewEnabled;
        this.hasRolePermissions = Permissions.checkUserPermission(ObjectActionListType.ROLE, ActionType.CREATE);
        if (this.isExpressSystem) {
            this.getReefExpressSettings();
        }
    }

    getReefExpressSettings() {
        this.restService.postWithObservable(Endpoint.orgManager_getReefExpressSettings, { handle: this.commonService.contextHandle }).subscribe({
            next: (response: any) => {
                if (response?.autoProjectCreation) {
                    this.reviewSystem = response.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Reef_Review ? ReviewSystem.REEF_REVIEW :
                        response.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Relativity ? ReviewSystem.RELATIVITY : ReviewSystem.NONE;
                    this.onChange();
                }
            }
        });
    }

    getAvailableProjectIDs(): Promise<any> {
        if (!this.availableReviewProjects || this.availableReviewProjects.length === 0) {
            this.isBusy = true;
            this.isBusyMessage = 'Loading ...';
            return new Promise<any>((resolve) => {
                this.restService.postWithObservable(Endpoint.projectManager_getReviewProjectList,
                    {
                        reviewSortBy: 'REVIEW_PROJECT_NAME',
                        reviewSystem: this.reviewSystem,
                    }).subscribe({
                        next: (response: any) => {
                            this.isBusy = false;
                            if (response?.reviewClientProjects) {
                                this.availableReviewProjects = response.reviewClientProjects;
                            }
                            return resolve(true);
                        }
                    });
            });
        } else {
            return Promise.resolve(true);
        }
    }

    loadRoles(): void {
        this.restService.post(Endpoint.orgManager_listRoles, {
            objectType: 'ALL',
            returnObjectActionLists: false
        }).then(responseListRoles => {
            if (responseListRoles && responseListRoles.roles) {
                this.roleList = responseListRoles.roles;
            }
            this.requestInfo.data = { organizationName: '' };
            this.genericGrid.loadData();
        });
    }

    loadTemplates(): void {
        this.eDiscoveryTemplateTypes = {
            ALIAS_LISTS: {
                name: 'Alias Lists:',
                automationId: 'new-project-alias-option',
                templates: [],
                selectedTemplate: null
            },
            ANALYTICAL_SETTINGS: {
                name: 'Analytic Settings:',
                automationId: 'new-project-analytic-option',
                templates: [],
                selectedTemplate: null
            },
            BILLING_REPORT_SETTINGS: {
                name: 'Billing Reports:',
                automationId: 'new-project-billing-option',
                templates: [],
                selectedTemplate: null
            },
            CUSTOM_FIELDS: {
                name: 'Custom Fields:',
                automationId: 'new-project-custom-option',
                templates: [],
                selectedTemplate: null
            },
            DOMAIN_LISTS: {
                name: 'Domain Lists:',
                automationId: 'new-project-domain-option',
                templates: [],
                selectedTemplate: null
            },
            EMAIL_SIGNATURE: {
                name: 'Excluded Content:',
                automationId: 'new-project-excluded-option',
                templates: [],
                selectedTemplate: null
            },
            EXPORT_FIELDS: {
                name: 'Export Fields:',
                automationId: 'new-project-export-fields-option',
                templates: [],
                selectedTemplate: null
            },
            EXPORT_SETTINGS: {
                name: 'Export Settings:',
                automationId: 'new-project-export-settings-option',
                templates: [],
                selectedTemplate: null
            },
            INDEX_SETTINGS: {
                name: 'Index Settings:',
                automationId: 'new-project-index-settings-option',
                templates: [],
                selectedTemplate: null
            },
            LOADFILE_SETTINGS: {
                name: 'Load File Import:',
                automationId: 'new-project-load-file-option',
                templates: [],
                selectedTemplate: null
            },
            SEARCH_FIELDS: {
                name: 'Metadata Search Fields:',
                automationId: 'new-project-metadata-search-fields-option',
                templates: [],
                selectedTemplate: null
            },
            DOCUMENT_METADATA: {
                name: 'Metadata View Fields:',
                automationId: 'new-project-metadata-view-fields-option',
                templates: [],
                selectedTemplate: null
            },
            USER_EXP: {
                name: 'Patterns:',
                automationId: 'new-project-patterns-option',
                templates: [],
                selectedTemplate: null
            },
            REPORT_SETTINGS: {
                name: 'Reports:',
                automationId: 'new-project-reports-option',
                templates: [],
                selectedTemplate: null
            },
            SEARCH_SETTINGS: {
                name: 'Search Settings:',
                automationId: 'new-project-search-settings-option',
                templates: [],
                selectedTemplate: null
            },
            DUPE_SURVIVORSHIP: {
                name: 'Survivorship Model:',
                automationId: 'new-project-survivorship-model-option',
                templates: [],
                selectedTemplate: null
            },
            TAG: {
                name: 'Tags:',
                automationId: 'new-project-tag-option',
                templates: [],
                selectedTemplate: null
            },
            WORKFLOWS: {
                name: "Workflows:",
                automationId: "new-project-workflows-option",
                templates: [],
                selectedTemplate: null,
            },
        };
        this.reviewTemplateTypes = this.commonService.isReview ? {
            REVIEW_SET_TEMPLATE: {
                name: 'Review Set:',
                automationId: 'new-project-review-sets-option',
                templates: [],
                selectedTemplate: null
            }
        } : null;

        let hasOrgPermissions = false;
        for (const value in ObjectActionListType) {
            if (value.includes('ORG_')) {
                hasOrgPermissions = Permissions.checkUserPermission(<ObjectActionListType>value);
                if (hasOrgPermissions) {
                    break;
                }
            }
        }
        this.hasSysPermissions = false;
        for (const value in ObjectActionListType) {
            if (value.includes('SYSTEM_')) {
                this.hasSysPermissions = Permissions.checkUserPermission(<ObjectActionListType>value);
                if (this.hasSysPermissions) {
                    break;
                }
            }
        }
        if (hasOrgPermissions || this.hasSysPermissions) {
            this.isSettingsBusy = true;
            const singleScope = this.hasSysPermissions ? 'SYSTEM_LEVEL' : 'ORG_LEVEL';
            this.restService.post(Endpoint.orgManager_listTemplates, {
                scope: this.hasSysPermissions && hasOrgPermissions ? 'SYSTEM_AND_ORG' : singleScope,
                tempType: null,
                organizationName: this.commonService.currentUser.customerName,
                systemScope: this.hasSysPermissions
            }).then(response => {
                if (response) {
                    this.isSettingsBusy = false;
                    if (response.templates) {
                        response.templates.forEach(template => {
                            if (this.eDiscoveryTemplateTypes[template.templateType]) {
                                this.eDiscoveryTemplateTypes[template.templateType].templates.push(template);
                                if (template.defaultTemplate) {
                                    this.eDiscoveryTemplateTypes[template.templateType].selectedTemplate = template;
                                }
                            } else if (this.reviewTemplateTypes && this.reviewTemplateTypes[template.templateType]) {
                                this.reviewTemplateTypes[template.templateType].templates.push(template);
                                if (template.defaultTemplate) {
                                    this.reviewTemplateTypes[template.templateType].selectedTemplate = template;
                                }
                            }
                        });
                        this.sortTemplatesAlphanumericallyByName();
                    }
                }
            }, () => {
                this.isSettingsBusy = false;
            });
        }
    }

    openSelectReviewProjects(): void {
        this.getAvailableProjectIDs().then(validProjectID => {
            if (this.modalOpened) {
                this.availableReviewProjects.forEach(project => project.assigned = false);
                this.selectedReviewProjects.forEach(selectedProject => {
                    const matchedItem = this.availableReviewProjects.find(item => item.reviewProject.id === selectedProject.reviewProject.id);
                    if (matchedItem) {
                        matchedItem.assigned = true;
                    }
                })
                this.commonService.openModal(SelectReviewProjectsModalComponent, {
                    class: 'horizontal-percent-60 vertical-percent-60',
                    modalData: { items: this.availableReviewProjects, reviewSystem: this.reviewSystem }
                }).then((result) => {
                    if (result.modalData?.length > 0) {
                        result.modalData.forEach(item => {
                            this.selectedReviewProjects.push(item);
                        })
                    }
                });
            }
        })
    }

    openSelectReviewTemplate(): void {
        this.commonService.openModal(SelectReviewTemplateModalComponent, {
                class: 'horizontal-percent-50 vertical-percent-50',
                modalData: { selectedTemplate: this.selectedTemplate, availableReviewTemplates: this.availableReviewTemplates }
            }).then((result) => {
                if (result.modalData?.length > 0) {
                    this.selectedTemplate = result.modalData[0].template.name;
                }
            });
    }

    onRemoveClick(item): void {
        const index = this.selectedReviewProjects.findIndex(prj => prj.reviewProject.id === item.reviewProject.id);
        if (index > -1) {
            this.selectedReviewProjects.splice(index, 1);
        }
    }

    onTemplateRemoveClick(): void {
        this.selectedTemplate = null;
    }

    sortTemplatesAlphanumericallyByName(): void {
        for (let key in this.eDiscoveryTemplateTypes) {
            this.eDiscoveryTemplateTypes[key].templates.sort((a, b) => a.name.localeCompare(b.name));
        }
    }

    getUserRole(roleHandles: Array<string>): any {
        if (roleHandles && roleHandles.length > 0) {
            return this.roleList.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }

    editMembers(): void {
        this.commonService.openModal(MembershipModalComponent, {
            class: 'vertical-percent-80',
            modalData: { leftList: this.allMembers, rightList: this.genericGrid.getData(), membershipType: MembershipType.project, isNewProjectCreation: true }
        }).then((result) => {
            if (result.modalResult && result.modalData.length) {
                const members = [];
                result.modalData.forEach(element => {
                    members.push(element.data);
                });
                this.genericGrid.setData(members);
            }
        });
    }

    okEnabled(): boolean {
        return this.nameDescriptionComponent.formValid && !DrObject.isEmpty(this.nameDescriptionComponent.name) &&
            this.genericGrid && this.genericGrid.getData().length > 0 && !this.isBusy && this.userValid &&
            (this.reviewSystem === ReviewSystem.NONE || (this.autoGenerate === true && (this.reviewSystem === ReviewSystem.RELATIVITY || ( this.reviewSystem === ReviewSystem.REEF_REVIEW && this.selectedTemplate ))) || this.selectedReviewProjects.length > 0) &&
            ((!this.standardProjectType && this.logoModel.encodedData !== null) || this.standardProjectType);
    }

    onChange(reviewSystem: string = null) {
        if (reviewSystem === this.reviewSystem) {
            return;
        }
        if (reviewSystem) {
            this.reviewSystem = reviewSystem;
        }
        this.userValid = true;
        this.autoGenerate = true;
        this.availableReviewProjects = [];
        this.selectedReviewProjects = [];
        if (this.reviewSystem === ReviewSystem.REEF_REVIEW) {
            const promiseList = [];
            this.isBusy = true;
            this.isBusyMessage = 'Validating Current User ...';
            this.userValid = false;
            this.availableReviewTemplates = [];
            this.selectedTemplate = null;

            promiseList.push(this.restService.post(Endpoint.projectManager_validateReviewUser, { userEmail: this.commonService.currentUser.email, reviewSystem: this.reviewSystem }).then(response => {
                if (response?.valid) {
                    this.userValid = true;
                } else {
                    this.commonService.openErrorModal(response.extendedStatus);
                }
            }, error => {
            }));
            promiseList.push(this.restService.post(Endpoint.projectManager_getReviewProjectTemplateList, { reviewSystem: ReviewSystem.REEF_REVIEW }).then(response => {
                if (response?.reviewProjectTemplates?.length > 0) {
                    this.availableReviewTemplates = response.reviewProjectTemplates;
                    this.selectedTemplate = response.reviewProjectTemplates.find(pt => pt.defaultTemplate)?.template?.name;
                }
            }, error => {
            }));
            Promise.all(promiseList).then(() => {
                this.isBusy = false;
                this.isBusyMessage = ``;
            });
        }
    }

    ok(): void {
        this.nameDescriptionComponent.validate().then((value) => {
            if (value) {
                this.isBusy = true;
                this.isBusyMessage = 'Creating Project ...';
                const request = {
                    addToCaseData: false,
                    custodians: [],
                    name: this.nameDescriptionComponent.name,
                    description: this.nameDescriptionComponent.description,
                    attributes: [],
                    membersRequestMessage: { groups: [], users: [] },
                    projectLogoBytes: this.logoModel.encodedData,
                    logoFileName: this.logoModel.fileName,
                    systemScope: false,
                    reviewSystem: this.reviewSystem === ReviewSystem.NONE ? null : this.reviewSystem,
                    reviewProjectIds: this.reviewSystem === ReviewSystem.NONE || this.autoGenerate ? [] : this.selectedReviewProjects.map(item=> item.reviewProject.id),
                    reviewProjectTemplateName: this.reviewSystem === ReviewSystem.REEF_REVIEW && this.autoGenerate && this.selectedTemplate ? this.selectedTemplate : null
                };
                for (const key in this.eDiscoveryTemplateTypes) {
                    if (Object.prototype.hasOwnProperty.call(this.eDiscoveryTemplateTypes, key)) {
                        const element = this.eDiscoveryTemplateTypes[key];
                        if (element.selectedTemplate) {
                            request.attributes.push({ name: element.selectedTemplate.templateType, value: element.selectedTemplate.handle });
                        }
                    }
                }
                for (const key in this.reviewTemplateTypes) {
                    if (Object.prototype.hasOwnProperty.call(this.reviewTemplateTypes, key)) {
                        const element = this.reviewTemplateTypes[key];
                        if (element.selectedTemplate) {
                            request.attributes.push({ name: element.selectedTemplate.templateType, value: element.selectedTemplate.handle });
                        }
                    }
                }
                this.genericGrid.getData().forEach(member => {
                    const item = { name: member.name, roleHandles: member.selectedRole ? [member.selectedRole.handle] : [] };
                    if (member.type.includes('User')) {
                        request.membersRequestMessage.users.push(item);
                    } else if (member.type.includes('Group')) {
                        request.membersRequestMessage.groups.push(item);
                    }
                });
                this.restService.post(Endpoint.ecaManager_createCase, request).then(responseCreateCase => {
                    this.isBusy = false;
                    if (responseCreateCase && responseCreateCase.caseHandle) {
                        this.isBusy = true;
                        this.restService.post(Endpoint.projectManager_getProject, { handle: responseCreateCase.caseHandle }).then(responseGetProject => {
                            this.isBusy = false;
                            if (responseGetProject && responseGetProject.project) {
                                this.closeModal(true, responseGetProject.project);
                            }
                        }, () => {
                            this.isBusy = false;
                        });
                    }
                }, () => {
                    this.isBusy = false;
                });
            } else {
                this.isBusy = false;
            }
        });
    }

    onProjectTypeChanged() {
        if (this.standardProjectType && this.logoModel.encodedData !== null) {
            this.logoModel.encodedData = null;
            this.logoModel.fileName = "";
        }
    }
}
